import React, { useEffect, useState } from 'react';
import { Anchor, Button } from '@keplerco/core';
import { FieldValues, useForm } from 'react-hook-form';
import classNames from 'classnames';
import { IDetailsStepLayoutProps } from './details-step.models';
import layoutStyles from '../../../../design/layout.module.css';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import assessmentsWizardStyles from '../../assessments-wizard.module.css';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { ExitModal } from '../../../../components/modals/exit.modal';
import { AssessmentSubType, AssessmentType } from '../../../../enums/assessment-type';
import { useAppActions, useAppState } from '../../../../overmind';
import { AssessmentRequest } from '../../../../overmind/actions';
import { AssessmentStatus } from '../../../../enums/assessment-status';
import { PagePath } from '../../../../navigation/navigation.enums';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { DetailsWidget } from '../details.widget';

export function DetailsStepLayout({ assessmentSlug, isLoading, setIsLoading, onNext, ...props }: IDetailsStepLayoutProps): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const { control, handleSubmit, formState, watch, reset, trigger } = useForm({ mode: 'onBlur' });

  const [fieldValues, setFieldValues] = useState<FieldValues>();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(formState.isDirty);
  const [isSaveDraft, setIsSaveDraft] = useState<boolean | null>(null);
  const [savedDetailsRequest, setSavedDetailsRequest] = useState<AssessmentRequest | null>(null);

  useEffect(() => {
    if (isSaveDraft === null || !savedDetailsRequest) return;

    if (isSaveDraft) {
      actions.dismissAllNotifications();
      actions.addNotification({
        id: crypto.randomUUID(),
        active: true,
        priority: NotificationPriority.Toast,
        toastIncludeIcon: true,
        type: NotificationType.Warn,
        title: 'This assessment is now a draft',
        message: 'You will need to schedule this once your changes are complete',
      });
      keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`);
    } else {
      onNext(savedDetailsRequest);
    }
    setIsSaveDraft(null);
    setSavedDetailsRequest(null);
    setIsLoading(false);
  }, [isSaveDraft, savedDetailsRequest]);

  useEffect(() => {
    watch(setFieldValues);
  }, []);

  useEffect(() => {
    reset(props.assessment);
  }, [props.assessment]);

  useEffect(() => {
    setHasUnsavedChanges(formState.isDirty);
  }, [formState.isDirty]);

  async function saveDetails(isSaveDraft?: boolean) {
    setIsLoading(true);

    const request: AssessmentRequest = {
      assessmentType: AssessmentType.PeerEndorsement,
      assessmentStatus: props.assessment?.assessmentStatus ?? AssessmentStatus.Draft,
      companySlug: companyVariables.slug!,
      slug: props.assessment?.slug,
      name: fieldValues?.name?.trim() ?? '',
      description: fieldValues?.description?.trim() ?? '',
      allowReassessment: false,
      forceAssessment: false,
      assessmentSubType: AssessmentSubType.RatingAssessment,
    };
    const slug = await actions.updateAssessment(request);
    if (!slug) {
      setIsLoading(false);
      return;
    }

    request.slug = slug;
    setHasUnsavedChanges(false);
    setSavedDetailsRequest(request);
    setIsSaveDraft(isSaveDraft ?? false);
  }

  return (
    <React.Fragment>
      <form id="assessmentDetails" className={assessmentsWizardStyles.stepContainer} onSubmit={handleSubmit(() => saveDetails())}>
        <SkeletonOverlay isLoading={isLoading}>
          <DetailsWidget control={control} />
        </SkeletonOverlay>

        <footer className={classNames('card', layoutStyles.wizardFooter)}>
          <Anchor
            reverse
            arrow
            aria-label="Return to previous page"
            onClick={() =>
              keplerNavigate(!!assessmentSlug ? `${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}` : `${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreate.replace(':companySlug', companyVariables.slug!)}`)
            }
          >
            Cancel
          </Anchor>

          <div className={assessmentsWizardStyles.stepFooterButtonGroup}>
            <Anchor
              disabled={isLoading}
              aria-label="Save as draft and exit"
              onClick={async () => {
                const isValid = await trigger();
                if (!isValid) return;
                saveDetails(true);
              }}
            >
              Save draft
            </Anchor>

            <Button disabled={isLoading} filled type="button" arrow aria-label="Save and continue to next step">
              Next
            </Button>
          </div>
        </footer>
      </form>

      <ExitModal hasUnsavedChanges={hasUnsavedChanges} />
    </React.Fragment>
  );
}
